/**
 * Data Table
 * Reusuable data table that can display generic data in a specific format
 * configured by the column object
 * Flexable and allows pagination
 */
import { Box } from '@mui/material';
import React, { useState, isValidElement, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ConversionToggle from '@components/ConversionToggle/ConversionToggle';
import FilterButton from '@components/FilterButton';
import Icon from '@components/Icon/Icon';
import NullStateDiv from '@components/NullStateDiv';
import {
  HorizontalFlex,
  VerticalFlex,
} from '@components/RecordCard/RecordCard.style';
import RelatedRecordsButton from '@components/RelatedRecordsTable/RelatedRecordsButton';

import { useIsMobile } from '@hooks/useIsMobile';

import type { FhirResource } from '@models/fhir-resource';

import { formatDate, formatTime } from '@utils/format';

import {
  ArrowContainer,
  NavArrow,
  NavContainer,
  PageNumber,
  Table,
  TableTitle,
} from './DataTable.style';

export type DataTableColumn = {
  name: string;
  title?: React.ReactNode;
  style?: string;
  desktopOnly?: boolean;
  canFilter?: boolean;
  show?: boolean;
  render?: (value: any) => React.ReactNode;
};

export type DataTableType = Date | React.ReactNode | FhirResource[] | undefined;
export type DataTableRecord = Record<string, DataTableType>;

const defaultColumn: DataTableColumn = {
  name: '',
  title: '',
  style: 'default',
  desktopOnly: false,
  canFilter: false,
  show: true,
};

const PAGE_SIZE = 10;

const messages = defineMessages({
  noData: {
    id: 'datatable.noData',
    defaultMessage: 'NO {type}',
  },
});

interface DataTableBorderProps {
  showBorder: boolean;
  tableBackgroundColor?: string;
  boxShadow?: string;
}

interface DataTableProps {
  data: DataTableRecord[];
  columns?: DataTableColumn[];
  showHead?: boolean;
  showFilter?: boolean;
  showConversion?: boolean;
  conversionType?: string;
  conversionGroup?: string | string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  title?: string;
  onClick?: (clicked: any) => void;
  showActionIcon?: boolean;
  showFilterPopper?: boolean;
  type?: string;
  border?: DataTableBorderProps;
  noHeader?: boolean;
}

function DataTable({
  border,
  data = [],
  columns = [],
  showHead = false,
  showFilter = false,
  showConversion = false,
  conversionType,
  conversionGroup,
  sortBy = '',
  sortOrder = 'desc',
  title = '',
  onClick,
  showActionIcon = false,
  showFilterPopper = false,
  type = '',
  noHeader = false,
}: DataTableProps) {
  const [sortingBy, setSortingBy] = useState(sortBy);
  const [sortingOrder, setSortingOrder] = useState<'asc' | 'desc'>(sortOrder);
  const [filteredData, setFilteredData] = useState(data);
  const [tableColumns, setTableColumns] = useState(columns);
  const [page, setPage] = useState(0);
  const mobile = useIsMobile();
  const { formatMessage } = useIntl();

  useEffect(() => {
    // Columns
    let newColumns = columns;

    // Default header dump
    if (data.length && newColumns.length === 0) {
      newColumns = Object.keys(data[0]).map((k) => ({
        name: k,
        title: k,
        style: 'default',
        desktopOnly: false,
      }));
    }

    // Completeness
    newColumns = newColumns.map((d) => ({ ...defaultColumn, ...d }));
    setTableColumns(newColumns.filter(({ show }) => show));
    setFilteredData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sortBy, sortOrder]);

  const changePage = (offset: number) => {
    const newPage = page + offset;
    if (newPage >= 0 && newPage < Math.ceil(filteredData.length / PAGE_SIZE)) {
      setPage(newPage);
    }
  };

  // Sort data
  const sortByKey = (sortBy?: string) => {
    if (!sortBy) {
      return;
    }
    setSortingBy(sortBy);
    setSortingOrder(
      sortingOrder === 'desc' || sortBy !== sortingBy ? 'asc' : 'desc'
    );
  };

  const sortData = (a: DataTableRecord, b: DataTableRecord) => {
    const valueA = a[sortingBy];
    const valueB = b[sortingBy];
    const column = tableColumns.find((c) => c.name === sortingBy);

    if (!column) {
      return -1;
    }

    // Dates
    if (column.style === 'date') {
      let cmprA = valueA;
      let cmprB = valueB;

      if (sortingOrder === 'asc') {
        cmprA = valueB;
        cmprB = valueA;
      }

      if (!(cmprA instanceof Date)) {
        return 1;
      }
      if (!(cmprB instanceof Date)) {
        return -1;
      }

      return new Date(cmprB).getTime() - new Date(cmprA).getTime();
    }

    if (column.style === 'time') {
      let cmprA = valueA;
      let cmprB = valueB;

      if (sortingOrder === 'asc') {
        cmprA = valueB;
        cmprB = valueA;
      }

      if (!(cmprA instanceof Date)) {
        return 1;
      }
      if (!(cmprB instanceof Date)) {
        return -1;
      }

      const today = new Date();
      const date1 = cmprB;
      const date2 = cmprA;

      date1.setDate(today.getDate());
      date1.setMonth(today.getMonth());
      date1.setFullYear(today.getFullYear());

      date2.setDate(today.getDate());
      date2.setMonth(today.getMonth());
      date2.setFullYear(today.getFullYear());

      return new Date(date1).getTime() - new Date(date2).getTime();
    }

    // Numbers
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      if (sortingOrder === 'asc') {
        return valueA - valueB;
      }
      return valueB - valueA;
    }

    // Strings
    if (sortingOrder === 'asc') {
      return String(valueA).localeCompare(String(valueB));
    }
    return String(valueB).localeCompare(String(valueA));
  };

  const dataString = (value: DataTableType, style?: string) => {
    if (value instanceof Date && style === 'date')
      return formatDate({ date: value, formatter: 'date' });
    if (value instanceof Date && style === 'time')
      return formatTime(value) || '-';
    if (isValidElement(value)) return value;
    if (typeof value === 'object') return null;
    return value ?? '-';
  };

  const renderEntry = ({
    render,
    value,
    style,
    desktopOnly,
    show,
  }: {
    style?: string;
    desktopOnly?: boolean;
    value: DataTableType;
    render?: (value: DataTableType) => React.ReactNode;
    show?: boolean;
  }) => {
    if (mobile && desktopOnly && show) return null;

    if (render instanceof Function) {
      return render(value);
    }
    return dataString(value, style);
  };

  return (
    <>
      {!noHeader && (
        <TableTitle>
          <HorizontalFlex
            justifyContent="space-between"
            marginBottom={0}
            marginTop={0}
            width="100%"
          >
            <VerticalFlex alignItems="flex-start">
              {!showConversion && (
                <h2 data-testid="datatable-title">{title}</h2>
              )}
              {showConversion && conversionType && conversionGroup ? (
                <ConversionToggle
                  group={conversionGroup}
                  type={conversionType}
                />
              ) : null}
            </VerticalFlex>
            <VerticalFlex alignItems="flex-end">
              {showFilter ? (
                <FilterButton
                  fields={tableColumns.filter(
                    (col) => col.name && col.title && col.canFilter
                  )}
                  items={data}
                  mode={showFilterPopper ? 'popper' : 'modal'}
                  onChange={(returnedData) => setFilteredData(returnedData)}
                />
              ) : null}
              {showActionIcon ? <RelatedRecordsButton /> : null}
            </VerticalFlex>
          </HorizontalFlex>
        </TableTitle>
      )}

      <Box
        sx={({ palette }) => ({
          ...(border && {
            border: `1px solid ${palette.divider}`,
            borderRadius: '1%',
          }),
        })}
      >
        <Table
          backgroundColor={border?.tableBackgroundColor}
          boxShadow={border?.boxShadow}
          id="t"
        >
          <colgroup>
            {tableColumns.map(({ name }) => (
              <col key={`table-col-group-${name}`} />
            ))}
          </colgroup>

          {showHead && filteredData.length > 0 ? (
            <thead>
              <tr>
                {tableColumns.map(({ title, name }) => (
                  <th
                    key={`table-header-${name}`}
                    onClick={() => title && sortByKey(name)}
                  >
                    {title}
                    {sortingBy === name && (
                      <ArrowContainer inverted={sortingOrder === 'asc'}>
                        <Icon
                          clickable
                          color="text.primary"
                          name="chevron-down"
                        />
                      </ArrowContainer>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}

          <tbody>
            {filteredData.length > 0 ? (
              filteredData
                .sort(sortData)
                .filter(
                  (_, index) =>
                    index >= page * PAGE_SIZE && index < (page + 1) * PAGE_SIZE
                )
                .map((dat, index) => (
                  <tr
                    key={`table-body-${index}`}
                    onClick={() => onClick?.(dat)}
                    style={{ cursor: onClick ? 'pointer' : 'default' }}
                  >
                    {tableColumns.map(
                      ({ render, style, desktopOnly, name, show }, index2) => (
                        <td
                          className={style}
                          key={`table-body-${name}-${index2}`}
                        >
                          {show
                            ? renderEntry({
                                render,
                                value: dat[name],
                                style,
                                desktopOnly,
                              })
                            : null}
                        </td>
                      )
                    )}
                  </tr>
                ))
            ) : (
              <tr>
                <td
                  colSpan={tableColumns.length}
                  style={{ padding: 0, cursor: 'default' }}
                >
                  <NullStateDiv>
                    {formatMessage(messages.noData, {
                      type: type.toUpperCase() || 'DATA',
                    })}
                  </NullStateDiv>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Box>

      {filteredData.length > PAGE_SIZE && (
        <NavContainer>
          <Box
            sx={({ palette }) => ({
              backgroundColor: palette.background.paper,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              overflow: 'hidden',
              borderRadius: '7px',
              ...(border?.showBorder && {
                border: `1px solid ${palette.divider}`,
                backgroundColor: border.tableBackgroundColor,
              }),
            })}
          >
            <NavArrow
              backgroundColor={border?.tableBackgroundColor}
              onClick={() => changePage(-1)}
            >
              <Icon
                clickable
                color="text.primary"
                name="chevron-down"
                rotation={90}
              />
            </NavArrow>
            <PageNumber>{`Page ${page + 1}/${Math.ceil(
              filteredData.length / PAGE_SIZE
            )}`}</PageNumber>
            <NavArrow
              backgroundColor={border?.tableBackgroundColor}
              onClick={() => changePage(1)}
            >
              <Icon
                clickable
                color="text.primary"
                name="chevron-down"
                rotation={-90}
              />
            </NavArrow>
          </Box>
        </NavContainer>
      )}
    </>
  );
}

export default DataTable;
